import { ChangeEvent, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { useProgramRequirements } from '@/app/hooks/useProgramRequirements'
import useApiDispatch from '@/hooks/useApiDispatch/useApiDispatch'
import {
  selectInProgressEnrollment,
  setUtilityAccountNumber,
} from '@/reducers/inProgressUtilityProgramEnrollments'
import { useMultiProgramRouteContext } from '@/app/pages/multi-program-enrollment'
import {
  UPLOAD_UTILITY_BILL_SUCCESS,
  uploadUtilityBillForm,
} from '@/actions/plans'
import { RSAAResultAction } from 'redux-api-middleware'
import { utilityAccountsCollection } from '@/reducers/utilityAccounts'
import { useFormik } from 'formik'
import * as yup from 'yup'
import {
  isUtilityAccountNumberCompleted,
  getAlphanumericLength,
} from '@/app/pages/multi-program-enrollment/utils/utils'
import { UseFormRegister } from 'react-hook-form'
import { currentUser } from '@/reducers'
import { User } from '@/types'
import { programsNewCollection } from '@/reducers/programsNew'

function isValidExtractedValue(
  value: string | undefined | null,
): value is string {
  if (!value) return false
  return value.toLowerCase() !== 'null'
}

export function useViewModel() {
  const { utilityProgram } = useMultiProgramRouteContext()
  const { routeToNextStep } = useProgramRequirements(utilityProgram)
  const dispatch = useAppDispatch()
  const home = useAppSelector((state) => state.homes?.selectedHome)
  const { apiDispatch } = useApiDispatch()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const user = useAppSelector(currentUser)
  const [fileUploadStatus, setFileUploadStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle')
  const [fileUploadName, setFileUploadName] = useState<string | null>(null)
  const [uploadErrorMessage, setUploadErrorMessage] = useState<{
    title: string
    subtitle: string
  } | null>(null)
  const [extractedAccountNumber, setExtractedAccountNumber] = useState<
    string | null
  >(null)
  const [utilityBillExampleModalOpen, setUtilityBillExampleModalOpen] =
    useState(false)

  const inProgressEnrollment = useAppSelector(
    selectInProgressEnrollment(utilityProgram.id),
  )

  const { data: utilityAccountData } = utilityAccountsCollection.useFetch(
    '/utilities/utility_account',
  )

  const existingAccountNumber =
    (utilityAccountData?.[0]?.account_number as string | undefined) ?? null

  const viewConfig = utilityProgram.view_config_json
  const accountNumberLength =
    viewConfig?.accountNumberEntry?.accountNumberLength ?? null
  const accountNumberLabel =
    viewConfig?.accountNumberEntry?.accountNumberLabel ?? 'Account Number'
  const utilityBillExampleSrc =
    viewConfig?.accountNumberEntry?.utilityBillExampleImage
  const utilityWebsiteLink = useAppSelector(
    (state) => state.utilities.selectedUtility?.website,
  )

  // Check if we should pre-populate with existing account number
  // Prefer in-progress enrollment, then existing account number if it matches length
  const initialAccountNumber =
    inProgressEnrollment?.utilityAccountNumber ??
    (existingAccountNumber &&
    isUtilityAccountNumberCompleted(
      inProgressEnrollment,
      existingAccountNumber,
      accountNumberLength ?? undefined,
    )
      ? existingAccountNumber
      : null)

  const validationSchema = yup.object({
    accountNumber: yup
      .string()
      .required('Utility account number is required')
      .test(
        'length',
        `Your ${accountNumberLabel} must be ${accountNumberLength} characters, not including spaces`,
        (value) => {
          if (!accountNumberLength || !value) return true
          return getAlphanumericLength(value) === accountNumberLength
        },
      ),
  })

  const formik = useFormik({
    initialValues: {
      accountNumber: initialAccountNumber ?? '',
    },
    validationSchema,
    onSubmit: () => {
      handleNext()
    },
  })

  const uploadUtilityBill = async (file: File) => {
    setFileUploadStatus('loading')
    setUploadErrorMessage(null)
    setFileUploadName(null)
    setExtractedAccountNumber(null)

    const fileSizeInMB = file.size / 1024 / 1024

    // Client-side validation
    if (fileSizeInMB > 10) {
      setFileUploadStatus('error')
      setFileUploadName(file.name)
      setUploadErrorMessage({
        title: `${file.name} has an error.`,
        subtitle: 'File size is too large, upload a file smaller than 10MB.',
      })
      return
    }

    const data = new FormData()
    data.append('file', file)

    const uploadResponse: RSAAResultAction = await dispatch(
      uploadUtilityBillForm(data, true, false),
    )

    if (uploadResponse.type === UPLOAD_UTILITY_BILL_SUCCESS) {
      setFileUploadName(file.name)

      // Extract account number from utility bill upload response
      // The response uses the same state as current_user endpoint
      // Check both the response payload (updated user) and current state
      const updatedUser = (uploadResponse as { payload?: User }).payload ?? user

      const utilityBillUpload = updatedUser?.profile?.utility_bill_upload as
        | {
            extraction?: { account_number?: string }
          }
        | null
        | undefined

      const extractedAccountNumber =
        utilityBillUpload?.extraction?.account_number

      if (isValidExtractedValue(extractedAccountNumber)) {
        // Extracted account number was found
        setFileUploadStatus('success')
        setUploadErrorMessage(null)
        setExtractedAccountNumber(extractedAccountNumber)
        formik.setFieldValue('accountNumber', extractedAccountNumber, true)
        dispatch(
          setUtilityAccountNumber({
            utilityProgramId: utilityProgram.id,
            utilityAccountNumber: extractedAccountNumber,
          }),
        )
      } else {
        // Extracted account number was not found
        setFileUploadStatus('error')
        setUploadErrorMessage({
          title: `${file.name} has an error.`,
          subtitle:
            "Account number not found, please make sure it's visible on the bill. See example below.",
        })
      }
    } else {
      // Unexpected error occurred when hitting the upload utility bill endpoint
      setFileUploadStatus('error')
      setFileUploadName(file.name)

      setUploadErrorMessage({
        title: `An unexpected error occurred.`,
        subtitle:
          'Please try again or enter your account number manually below.',
      })
    }
  }

  const handleFilesUploaded = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) {
      return
    }

    await uploadUtilityBill(files[0])
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const register = ((name: string) => {
    return {
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange(event)
      },
      onBlur: (event: React.FocusEvent<HTMLInputElement>) => {
        formik.handleBlur(event)
      },
      name,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ref: (_element: HTMLInputElement | null) => {
        // No-op ref for formik compatibility
      },
    }
  }) as unknown as UseFormRegister<any>

  const handleNext = () => {
    if (formik.values.accountNumber && !formik.errors.accountNumber) {
      const response = apiDispatch(
        utilityAccountsCollection.actions.update(
          `/utilities/utility_account/${home?.id}`,
          { account_number: formik.values.accountNumber },
          { id: home?.id },
        ),
      )

      response.then((result) => {
        if (result.ok) {
          dispatch(programsNewCollection.actions.invalidate())
          dispatch(
            setUtilityAccountNumber({
              utilityProgramId: utilityProgram.id,
              utilityAccountNumber: formik.values.accountNumber,
            }),
          )
          routeToNextStep()
        } else {
          formik.setFieldError(
            'accountNumber',
            'There was an error updating your account number. Please contact support@optiwatt.com if the problem persists.',
          )
        }
      })
    }
  }

  const canProceed =
    formik.values.accountNumber &&
    !formik.errors.accountNumber &&
    !formik.isSubmitting

  return {
    fileInputRef,
    handleFilesUploaded,
    handleUploadClick,
    fileUploadStatus,
    fileUploadName,
    uploadErrorMessage,
    extractedAccountNumber,
    formik,
    register,
    accountNumberLength,
    accountNumberLabel,
    utilityBillExampleSrc,
    utilityWebsiteLink,
    utilityBillExampleModalOpen,
    setUtilityBillExampleModalOpen,
    handleNext,
    canProceed,
    initialAccountNumber,
  }
}
